import * as React from 'react';
import styled from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import { FlexCol } from '../../Defaults/Flexbox/Flexbox';
import { StockInfo } from '../../Autohaus/Product/StockInfo';
import { TableTd } from '../../Zemplin/General/Table';
import { ProductPriceCartTable } from './ProductPriceCartTable';
import { round } from '../../../utilities';

export interface Props {
  product: any;
  addToCart: any;
  addToRequest: any;
  rowsData: string[];
  isAdding: boolean;
  isB2B: boolean;
}

function ProductTabRowTable({
  product,
  addToCart,
  addToRequest,
  rowsData,
  isAdding,
  isB2B,
}: Props) {
  const [count, setCount] = React.useState(1);

  const handleCountChange = newVal => {
    setCount(newVal);
  };

  const {
    onStock,
    name,
    price,
    unit,
    availability,
    oldPrice,
    mocPrice,
    priceWithoutVat,
  } = getProductDefaultInfo(product);

  const handleAddToCart = () => {
    addToCart(product, count);
  };

  const handleAddToRequest = () => {
    addToRequest(product, count);
  };

  const renderName = () => {
    return (
      <ImageNameTableTd>
        <NameInputWrapper>
          <Name>{name}</Name>
        </NameInputWrapper>
      </ImageNameTableTd>
    );
  };

  const renderStock = () => {
    return (
      <StyledTableTd>
        <StyledStockInfo
          colorMapTable={{
            '1': 'rgb(45, 204, 141)',
            '4': 'rgb(255, 45, 88)',
            '2': 'rgb(78,79,255)',
          }}
          availability={availability}
          onStock={onStock}
          unit={unit}
          withoutInfo={true}
          withoutExactStockCount={true}
        />
      </StyledTableTd>
    );
  };

  const renderPrice = () => {
    return (
      <PriceTableTd align={'right'}>{`${price} € / ${unit}`}</PriceTableTd>
    );
  };

  const renderMocPrice = () => {
    return (
      <PriceTableTd align={'right'}>{`${mocPrice} € / ${unit}`}</PriceTableTd>
    );
  };

  const renderOldPrice = () => {
    return (
      <>
        {oldPrice ? (
          <PriceTableTd
            align={'right'}
          >{`${oldPrice} € / ${unit}`}</PriceTableTd>
        ) : (
          <PriceTableTd align={'right'}>{''}</PriceTableTd>
        )}
      </>
    );
  };

  const renderCart = () => {
    return (
      <ProductPriceCartTable
        unit={unit}
        addToCart={handleAddToCart}
        addToRequest={handleAddToRequest}
        count={count}
        handleCountChange={handleCountChange}
        isAdding={isAdding}
      />
    );
  };

  return (
    <>
      {rowsData &&
        rowsData.map(dataName => {
          switch (dataName) {
            case 'name':
              return renderName();

            case 'stock':
              return renderStock();

            case 'price':
              return renderPrice();

            case 'oldprice':
              return renderOldPrice();

            case 'cart':
              return renderCart();

            case 'mocprice':
              return renderMocPrice();

            default:
              return null;
          }
        })}
    </>
  );
}

const prop = (object, path, defaultValue: any = null) => {
  const val = path.split('.').reduce((o, x) => {
    return typeof o === 'undefined' || o === null ? defaultValue : o[x];
  }, object);
  if (typeof val === 'undefined') {
    return defaultValue;
  }
  return val;
};

export function getProductDefaultInfo(product) {
  const availability = prop(product, 'main_good.availability', null);
  const ordernr = prop(product, 'main_good.order_number', '');
  const onStock = prop(product, 'main_good.on_stock_count', 0);
  const oldPrice = prop(
    product,
    'main_good.oldprice',
    prop(product, 'main_good.final_price'),
  );
  const price = prop(product, 'main_good.final_price');
  const priceWithoutVat = prop(product, 'main_good.final_price_without_vat');
  const unit = prop(product, 'main_good.units');
  const url = `/${product.url}`;
  const { name, picture, product_id: id } = product;
  const mainGoodId = prop(product, 'main_good.good_id');
  const mocPrice = prop(product, 'main_good.moc_price');

  return {
    ordernr,
    onStock,
    oldPrice,
    price: price ? round(price, 2).toFixed(2) : '',
    unit,
    url,
    id,
    goodId: mainGoodId,
    name,
    availability,
    mocPrice: mocPrice ? round(mocPrice, 2).toFixed(2) : '',
    priceWithoutVat,
    picture: picture
      ? `https://www.zemplin.sk/api/v1/image?query=${picture}&width=160&height=160&webp=0`
      : '/images/zemplin/placeholder.svg',
  };
}

const PriceTableTd = styled(TableTd)`
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-right: ${rem(6)};
  white-space: nowrap;
  padding: ${rem(24)} 0;
  /* border-top: ${({ theme }) => theme.borders.primary}; */
  border-bottom: ${({ theme }) => theme.borders.primary};
`;

const StyledStockInfo = styled(StockInfo)`
  margin: 0;
`;

const StyledTableTd = styled(TableTd)`
  height: 100%;
  padding: 0;
  /* border-top: ${({ theme }) => theme.borders.primary}; */
  border-bottom: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const ImageNameTableTd = styled(StyledTableTd)`
  height: 72px;
  display: flex;
  align-items: center;
  /* border-top: ${({ theme }) => theme.borders.primary}; */

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const NameInputWrapper = styled(FlexCol)`
  ${({ theme }) => theme.mediab.l1050`
      max-width: ${rem(130)};
  `}

  ${({ theme }) => theme.mediab.l925`
      max-width: ${rem(100)};
  `}
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.black};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 300;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }
`;

export { ProductTabRowTable };
